import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  User,
  Target,
  Calendar,
  CheckCircle,
  PlayCircle,
  Clock,
  Trophy,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import type { SessionDetail, SessionGame } from '../../../shared/session-types';

interface Game {
  id: number;
  codigo: string;
  nombre: string;
}

export default function PatientSessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSession();
    loadGames();
  }, [id]);

  const loadSession = async () => {
    try {
      const response = await api.get(`/patient/sessions/${id}`);
      setSession(response.data.data);

      // Si es la primera vez que accede a la sesión pendiente, marcarla como en_curso
      if (response.data.data.estado === 'pendiente') {
        await api.put(`/patient/sessions/${id}/start`);
        // Recargar sesión para obtener el nuevo estado
        const updatedResponse = await api.get(`/patient/sessions/${id}`);
        setSession(updatedResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Error al cargar la sesión');
      navigate('/paciente/sesiones');
    } finally {
      setLoading(false);
    }
  };

  const loadGames = async () => {
    try {
      const response = await api.get('/games');
      setGames(response.data.data);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const handlePlayGame = async (sessionGame: SessionGame) => {
    const game = games.find((g) => g.id === sessionGame.juego_id);
    if (!game) return;

    // Verificar si el juego anterior está completado (orden secuencial)
    const previousGame = session?.juegos.find(
      (g) => g.orden === sessionGame.orden - 1
    );

    if (previousGame && previousGame.estado !== 'completado') {
      toast.error('Debes completar el juego anterior primero');
      return;
    }

    // Marcar juego como en_curso si está pendiente
    if (sessionGame.estado === 'pendiente') {
      try {
        await api.put(`/patient/sessions/games/${sessionGame.id}/start`);
      } catch (error) {
        console.error('Error starting game:', error);
      }
    }

    // Navegar al juego con contexto de sesión
    navigate(
      `/juegos/${game.codigo}/${sessionGame.dificultad_id}?sesion_remota=${id}&sesion_juego=${sessionGame.id}`
    );
  };

  const getGameStatusColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-gray-500/20 text-gray-400';
      case 'en_curso':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'completado':
        return 'bg-green-500/20 text-green-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const isGameLocked = (game: SessionGame) => {
    if (game.orden === 1) return false;
    const previousGame = session?.juegos.find((g) => g.orden === game.orden - 1);
    return previousGame?.estado !== 'completado';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="glass-container mb-6">
        <button
          onClick={() => navigate('/paciente/sesiones')}
          className="glass-button px-4 py-2 mb-4 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Mis Sesiones
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Sesión #{session.id}</h1>
            <div className="flex items-center gap-2 text-gray-400">
              <User className="w-4 h-4" />
              <span>
                Profesional: {session.profesional_nombre}{' '}
                {session.profesional_apellidos}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 mt-1">
              <Calendar className="w-4 h-4" />
              <span>
                Creada: {new Date(session.fecha_creacion).toLocaleDateString()}
              </span>
            </div>
          </div>
          <span
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              session.estado === 'pendiente'
                ? 'bg-blue-500/20 text-blue-400'
                : session.estado === 'en_curso'
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-green-500/20 text-green-400'
            }`}
          >
            {session.estado}
          </span>
        </div>
      </div>

      {/* Objetivos */}
      {session.objetivos && (
        <div className="glass-container mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold">Objetivos de la Sesión</h3>
          </div>
          <p className="text-gray-300">{session.objetivos}</p>
        </div>
      )}

      {/* Observaciones Iniciales */}
      {session.observaciones_iniciales && (
        <div className="glass-container mb-6">
          <h3 className="text-lg font-semibold mb-3">Observaciones del Profesional</h3>
          <p className="text-gray-300">{session.observaciones_iniciales}</p>
        </div>
      )}

      {/* Progreso General */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-sm text-gray-400">Total Juegos</p>
              <p className="text-2xl font-bold">{session.stats.total_juegos}</p>
            </div>
          </div>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-sm text-gray-400">Completados</p>
              <p className="text-2xl font-bold text-green-400">
                {session.stats.juegos_completados}
              </p>
            </div>
          </div>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-sm text-gray-400">Progreso</p>
              <p className="text-2xl font-bold text-yellow-400">
                {session.stats.progreso_porcentaje}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Progreso */}
      <div className="glass-container mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Progreso General</span>
          <span className="text-sm font-medium">
            {session.stats.juegos_completados} / {session.stats.total_juegos}
          </span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${session.stats.progreso_porcentaje}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
          />
        </div>
      </div>

      {/* Lista de Juegos */}
      <div className="glass-container mb-6">
        <h3 className="text-lg font-semibold mb-4">Juegos Asignados</h3>

        <div className="space-y-3">
          {session.juegos.map((game, index) => {
            const locked = isGameLocked(game);

            return (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border transition-all ${
                  locked
                    ? 'bg-white/5 border-white/10 opacity-50'
                    : 'bg-white/10 border-white/20 hover:bg-white/15'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/20 text-blue-400 font-bold">
                      {game.orden}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{game.juego_nombre}</h4>
                      <p className="text-sm text-gray-400">
                        Dificultad: {game.dificultad_nombre}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getGameStatusColor(
                        game.estado
                      )}`}
                    >
                      {game.estado}
                    </span>
                  </div>

                  {locked ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Lock className="w-5 h-5" />
                      <span className="text-sm">Bloqueado</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handlePlayGame(game)}
                      className="glass-button px-6 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-semibold flex items-center gap-2"
                    >
                      {game.estado === 'completado' ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Repetir
                        </>
                      ) : game.estado === 'en_curso' ? (
                        <>
                          <PlayCircle className="w-4 h-4" />
                          Continuar
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4" />
                          Jugar
                        </>
                      )}
                    </button>
                  )}
                </div>

                {locked && (
                  <div className="mt-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-xs text-yellow-300">
                      ⚠️ Completa el juego anterior para desbloquear este
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Observaciones Finales (si la sesión está finalizada) */}
      {session.estado === 'finalizada' && session.observaciones_finales && (
        <div className="glass-container">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-green-400">
              Sesión Completada
            </h3>
          </div>
          <p className="text-gray-300 mb-2">
            <strong>Observaciones del profesional:</strong>
          </p>
          <p className="text-gray-300">{session.observaciones_finales}</p>
        </div>
      )}
    </div>
  );
}
